'use client'
import isHotkey from 'is-hotkey'
import React, {
  KeyboardEvent,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Editable, ReactEditor, useSlate } from 'slate-react'
import { RenderElement } from './components/render-element'
import { RenderLeaf } from './components/render-leaf'
import { HOTKEYS, toggleMark } from './funcitons'
import { handleListSoftBreak } from './plugins/with-lists'

type EditorInputProps = {
  placeholder?: string
  readOnly?: boolean
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>
}

export function EditorInput({
  placeholder,
  readOnly = false,
  onKeyDown,
}: EditorInputProps) {
  const editor = useSlate()
  const [hasFocused, setHasFocused] = useState(false)

  const renderElement = useCallback((props) => <RenderElement {...props} />, [])

  const renderLeaf = useCallback((props) => <RenderLeaf {...props} />, [])

  const defaultKeyBidings = (event: KeyboardEvent<HTMLDivElement>) => {
    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event)) {
        event.preventDefault()
        const mark = HOTKEYS[hotkey]
        toggleMark(editor, mark)
        return true
      }
    }
    return false
  }

  const onKeyDownHandler = (event: KeyboardEvent<HTMLDivElement>) => {
    if (defaultKeyBidings(event)) {
      return
    }
    // Shift+Enter dentro de una lista: soft-break (línea en blanco sin nuevo item)
    if (isHotkey('shift+enter', event) && handleListSoftBreak(editor)) {
      event.preventDefault()
      return
    }
    if (onKeyDown) onKeyDown(event)
  }

  // Solo enfocamos el editor después de que el usuario interactúe con él
  // en lugar de automáticamente al cargar la página
  useEffect(() => {
    if (!readOnly && editor && hasFocused) {
      ReactEditor.focus(editor as unknown as ReactEditor)
    }
  }, [readOnly, editor, hasFocused])

  return (
    <Editable
      renderElement={renderElement}
      renderLeaf={renderLeaf}
      placeholder={placeholder}
      spellCheck
      onKeyDown={onKeyDownHandler}
      readOnly={readOnly}
      onFocus={() => setHasFocused(true)}
    />
  )
}
